/**
 * AttributesPage — Catálogo · Atributos dinámicos por categoría.
 *
 * Define qué atributos existen para cada categoría (marca, modelo, año, motor,
 * código OEM…), su tipo y si son filtrables. Los VALORES por producto viven en
 * Product.attributes ({code: valor}); aquí solo se declaran las definiciones.
 */
import { useState, useEffect, useMemo } from 'react'
import {
  Plus,
  PencilLine,
  Trash2,
  Loader2,
  Tags,
  Filter,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { DataTable } from '@/components/ds/DataTable'
import { KebabMenu } from '@/components/KebabMenu'
import { ConfirmModal } from '@/components/ConfirmModal'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { ToastContainer, useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { extractFieldErrors } from '@/lib/errorUtils'
import { inputCls } from '@/lib/formStyles'
import { useProductCategories } from '@/modules/sales/hooks/useCatalog'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { Modal } from '@/components/ds/Modal'
import {
  useProductAttributes, useCreateProductAttribute, useUpdateProductAttribute,
  useDeleteProductAttribute, type ProductAttribute, type AttributeDataType,
} from '@/modules/sales/hooks/useCatalog'

const DATA_TYPES: { value: AttributeDataType; label: string }[] = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'select', label: 'Selección' },
  { value: 'boolean', label: 'Sí / No' },
]

type FormValues = {
  category: string
  name: string
  code: string
  data_type: AttributeDataType
  optionsText: string
  is_filterable: boolean
  is_required: boolean
  order: number
}

function slugify(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

function AttributeModal({ open, onClose, onSubmit, isPending, initial, categories, serverErrors, defaultCategory }: {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<ProductAttribute>) => void
  isPending: boolean
  initial?: ProductAttribute | null
  categories: { id: string; name: string; parent_name: string | null }[]
  serverErrors?: Record<string, string>
  defaultCategory?: string
}) {
  const { register, handleSubmit, reset, setError, watch, setValue, formState: { errors } } = useForm<FormValues>()
  const dataType = watch('data_type')
  const codeTouched = watch('code')

  useEffect(() => {
    if (open) {
      reset({
        category: initial?.category ?? defaultCategory ?? '',
        name: initial?.name ?? '',
        code: initial?.code ?? '',
        data_type: initial?.data_type ?? 'text',
        optionsText: (initial?.options ?? []).join('\n'),
        is_filterable: initial?.is_filterable ?? true,
        is_required: initial?.is_required ?? false,
        order: initial?.order ?? 0,
      })
    }
  }, [open, initial, defaultCategory, reset])

  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, message]) =>
        setError((field === 'options' ? 'optionsText' : field) as keyof FormValues, { type: 'server', message }))
    }
  }, [serverErrors, setError])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const wrapped = (v: FormValues) => onSubmit({
    category: v.category,
    name: v.name,
    code: v.code,
    data_type: v.data_type,
    options: v.data_type === 'select'
      ? v.optionsText.split('\n').map(o => o.trim()).filter(Boolean)
      : [],
    is_filterable: v.is_filterable,
    is_required: v.is_required,
    order: Number(v.order) || 0,
  })

  return (
    <Modal open onClose={onClose} title={initial?.id ? 'Editar atributo' : 'Nuevo atributo'} size="lg">
      <form onSubmit={handleSubmit(wrapped)} className="space-y-3">
        <SelectField
          dense
          label="Categoría"
          required
          disabled={!!initial?.id}
          selectProps={register('category', { required: 'Seleccionar una categoría.' })}
          error={errors.category?.message}
        >
          <option value="">— Selecciona —</option>
          {categories.map(c => (
          <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}</option>
          ))}
        </SelectField>

        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Nombre"
            required
            placeholder="Ej: Año fabricación"
            inputProps={{ ...register('name', {
              required: 'El nombre es obligatorio.',
              onChange: (e) => { if (!initial?.id && !codeTouched) setValue('code', slugify(e.target.value)) },
            }), autoFocus: true }}
            error={errors.name?.message}
          />
          <TextField
            dense
            label="Código"
            required
            placeholder="anio"
            disabled={!!initial?.id}
            inputProps={register('code', { required: 'El código es obligatorio.' })}
            hint="Clave interna (sin espacios). No se puede cambiar luego."
            error={errors.code?.message}
          />
        </div>

        <SelectField
          dense
          label="Tipo de dato"
          selectProps={register('data_type')}
        >
          {DATA_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </SelectField>

        {dataType === 'select' && (
          <TextField
            dense
            label="Opciones"
            required
            multiline
            rows={4}
            placeholder={'Una opción por línea\nGasolina\nDiésel\nEléctrico'}
            inputProps={register('optionsText')}
            hint="Una opción por línea."
            error={errors.optionsText?.message}
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input {...register('is_filterable')} type="checkbox"
              className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
            <span className="text-sm text-content">Filtrable</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input {...register('is_required')} type="checkbox"
              className="w-4 h-4 rounded text-primary border-divider-strong focus:ring-primary" />
            <span className="text-sm text-content">Requerido</span>
          </label>
        </div>

        <TextField
          dense
          label="Orden"
          placeholder="0"
          inputProps={{ ...register('order', { valueAsNumber: true }), type: 'number', step: '1', min: '0' }}
        />

        <div className="flex justify-end gap-3 pt-2 border-t border-divider">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
            Cancelar
          </button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
            {isPending && <Loader2 size={14} className="animate-spin" />}
            {initial?.id ? 'Guardar cambios' : 'Crear atributo'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function AttributesPage() {
  const toast = useToast()
  const { can } = usePermission()
  const canEdit = can('sales.products', 'edit')
  const canCreate = can('sales.products', 'create')

  const [categoryFilter, setCategoryFilter] = useState('')
  const { data: catData } = useProductCategories({ active: 'all' })
  const categories = useMemo(() => catData?.categories ?? [], [catData])

  const { data, isLoading } = useProductAttributes(
    categoryFilter ? { category: categoryFilter, active: 'all' } : { active: 'all' },
  )
  const attributes = data?.attributes ?? []

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ProductAttribute | null>(null)
  const [serverErrors, setServerErrors] = useState<Record<string, string>>()
  const [deleting, setDeleting] = useState<ProductAttribute | null>(null)

  const createMut = useCreateProductAttribute()
  const updateMut = useUpdateProductAttribute(editing?.id ?? '')
  const deleteMut = useDeleteProductAttribute()

  const handleSubmit = (payload: Partial<ProductAttribute>) => {
    setServerErrors(undefined)
    const onError = (e: unknown) => {
      const fields = extractFieldErrors(e)
      if (fields) setServerErrors(fields)
      else toast.error('No se pudo guardar el atributo. ¿Código duplicado en la categoría?')
    }
    if (editing?.id) {
      updateMut.mutate(payload, {
        onSuccess: () => { setModalOpen(false); toast.success('Atributo actualizado.') },
        onError,
      })
    } else {
      createMut.mutate(payload, {
        onSuccess: () => { setModalOpen(false); toast.success('Atributo creado.') },
        onError,
      })
    }
  }

  const handleDelete = () => {
    if (!deleting) return
    deleteMut.mutate(deleting.id, {
      onSuccess: () => { setDeleting(null); toast.success('Atributo eliminado.') },
      onError: () => { setDeleting(null); toast.error('No se pudo eliminar el atributo.') },
    })
  }

  const hasCategories = categories.length > 0

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Atributos"
        subtitle="Define los atributos dinámicos de cada categoría (marca, año, motor, código OEM…)"
        help={HELP['sales.attributes']}
      >
        {canCreate && hasCategories && (
          <button onClick={() => { setEditing(null); setServerErrors(undefined); setModalOpen(true) }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
            <Plus size={16} />Nuevo atributo
          </button>
        )}
      </PageHeader>

      <div className="mb-4 flex items-center gap-2">
        <Filter size={15} className="text-content-subtle" />
        <select aria-label="Todas las categorías" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className={`${inputCls} max-w-xs`}>
          <option value="">Todas las categorías</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} › ${c.name}` : c.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {!hasCategories ? (
          <EmptyState
            icon={Tags}
            title="Primero crea categorías"
            hint="Los atributos se definen por categoría. Crea una categoría antes de agregar atributos."
          />
        ) : (
          <DataTable<typeof attributes[number]>
            columns={[
              { key: 'name', header: 'Atributo', sortable: true,
                render: attr => (
                  <>
                    <p className="text-sm font-medium text-content">{attr.name}</p>
                    <p className="text-xs text-content-subtle font-mono">
                      {attr.code}{attr.is_required ? ' · requerido' : ''}
                    </p>
                    {attr.data_type === 'select' && attr.options.length > 0 && (
                      <p className="text-xs text-content-subtle mt-0.5 truncate max-w-xs">
                        {attr.options.join(', ')}
                      </p>
                    )}
                  </>
                ) },
              { key: 'category_name', header: 'Categoría', sortable: true,
                render: attr => <span className="text-content-muted">{attr.category_name}</span> },
              { key: 'data_type_display', header: 'Tipo', sortable: true,
                render: attr => (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-hover">
                    {attr.data_type_display}
                  </span>
                ) },
              { key: 'is_filterable', header: 'Filtrable', sortable: true,
                sortValue: attr => (attr.is_filterable ? 1 : 0),
                render: attr => (attr.is_filterable
                  ? <span className="text-xs font-medium text-success-content-soft">Sí</span>
                  : <span className="text-xs text-content-subtle">No</span>) },
              ...(canEdit ? [{
                key: 'acciones', header: '', width: 140,
                render: (attr: typeof attributes[number]) => (
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => { setEditing(attr); setServerErrors(undefined); setModalOpen(true) }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                      <PencilLine size={11} />Editar
                    </button>
                    <KebabMenu items={[
                      { label: 'Eliminar', icon: <Trash2 size={14} />, onClick: () => setDeleting(attr),
                        className: 'text-danger-content-soft hover:bg-danger-surface' },
                    ]} />
                  </div>
                ),
              }] : []),
            ]}
            rows={attributes}
            rowKey={attr => attr.id}
            minWidth={760}
            surface={false}
            loading={isLoading}
            loadingNode={<TableSkeleton rows={6} cols={5} />}
            empty={
              <EmptyState
                icon={Tags}
                title="Sin atributos"
                hint="Agrega atributos como marca, modelo, año o código OEM para esta categoría."
              />
            }
            caption="Atributos definidos por categoría"
          />
        )}
      </div>

      <AttributeModal
        key={editing?.id ?? 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        isPending={editing?.id ? updateMut.isPending : createMut.isPending}
        initial={editing}
        categories={categories}
        serverErrors={serverErrors}
        defaultCategory={categoryFilter || undefined}
      />

      <ConfirmModal
        open={deleting !== null}
        message={`¿Eliminar el atributo "${deleting?.name}"? Los valores ya guardados en productos quedarán huérfanos.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        isPending={deleteMut.isPending}
        variant="danger"
      />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}
